// src\features\Cart\components\CartSummary.tsx
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { formatCurrency } from "@/utils/formatCurrency";
import { useShallow } from "zustand/shallow";
import {
  useTransactionStore,
  selectCartTotal,
  selectGrandTotal,
  selectCartItemCount,
  selectAfterDiscount,
} from "../store/transactionStore";
import { Calendar, User, Receipt } from "lucide-react";
import EditModal from "./EditModal";
import { useState } from "react";
import { normalizeDateForInput } from "@/utils/normalizeDateForInput";

interface CartSummaryProps {
  onCancel: () => void;
  onPending: () => void;
}

export default function CartSummary({ onCancel, onPending }: CartSummaryProps) {
  const [editField, setEditField] = useState<
    "customer" | "discount" | "ppn" | null
  >(null);

  const [
    tanggalTransaksi,
    metodePembayaran,
    customer,
    customerName,
    diskonSubtotal,
    ppn,
    setTanggalTransaksi,
    setCustomer,
    setDiskonSubtotal,
    setPPN,
    setMetodePembayaran,
  ] = useTransactionStore(
    useShallow((state) => [
      state.tanggalTransaksi,
      state.metodePembayaran,
      state.customer,
      state.customerName,
      state.diskonSubtotal,
      state.ppn,
      state.setTanggalTransaksi,
      state.setCustomer,
      state.setDiskonSubtotal,
      state.setPPN,
      state.setMetodePembayaran,
    ]),
  );

  const navigate = useNavigate();

  const paymentRouteMap = {
    Cash: "/payment",
    Transfer: "/payment",
    QRIS: "/payment",
  };

  const totalQuantity = useTransactionStore(selectCartItemCount);
  const totalSubtotal = useTransactionStore(selectCartTotal);
  const totalFinal = useTransactionStore(selectGrandTotal);
  const afterDiscount = useTransactionStore(selectAfterDiscount);

  const [cust_name, setCust_name] = useState(customerName || "");

  const handleEdit = (
    field: "customer" | "discount" | "ppn",
    value: string,
    _type?: "percent" | "amount",
    other?: string,
  ) => {
    if (field === "customer") {
      setCust_name(other || "");
      setCustomer(value, other);
    }
    if (field === "discount") {
      setDiskonSubtotal(Number(value)); // sudah rupiah dari EditModal
    }
    if (field === "ppn") {
      setPPN(Number(value)); // sudah rupiah dari EditModal
    }
    setEditField(null);
  };

  return (
    <>
      <div className="bg-white dark:bg-[#1b1b1b] p-6 absolute rounded-t-3xl bottom-0 left-0 right-0 border-t-2 border-[#37393d]/10 shadow-2xl z-10">
        <div className="space-y-3">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-white">
              <Calendar className="w-4 h-4 dark:hidden" />
              <span>Tanggal {tanggalTransaksi}</span>
            </div>
            <input
              type="date"
              className="border border-gray-200 dark:border-[#37393d] rounded-lg px-3 py-1.5 text-sm font-medium"
              value={normalizeDateForInput(tanggalTransaksi)}
              onChange={(e) => setTanggalTransaksi(e.target.value)}
            />
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600 dark:text-white">
              <Receipt className="w-4 h-4 dark:hidden" />
              <span>Metode</span>
            </div>
            <div className="flex gap-1">
              {(["Cash", "Transfer", "QRIS"] as const).map((m) => (
                <button
                  key={m}
                  onClick={() => setMetodePembayaran(m)}
                  className={`px-2.5 py-1 rounded-full text-xs font-medium transition-all ${
                    metodePembayaran === m
                      ? "bg-[#d7d0fe] text-[#37393d]"
                      : "bg-gray-100 text-gray-500 hover:bg-gray-200 dark:bg-[#2a2a2a] dark:text-gray-400"
                  }`}
                >
                  {m}
                </button>
              ))}
            </div>
          </div>

          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 text-gray-600">
              <User className="w-4 h-4 dark:hidden" />
              <span>Customer</span>
            </div>
            <button
              onClick={() => setEditField("customer")}
              className="flex items-center gap-2 font-medium"
            >
              {cust_name}
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                ✎
              </span>
            </button>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span>Total Item</span>
            <span className="font-semibold">{totalQuantity}</span>
          </div>

          <div className="pt-3 mt-3 border-t" />

          <div className="flex items-center justify-between text-base">
            <span className="font-medium">Subtotal</span>
            <span className="font-semibold">
              {formatCurrency(String(totalSubtotal))}
            </span>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span>Diskon</span>
            <button
              onClick={() => setEditField("discount")}
              className="flex items-center gap-2 font-medium"
            >
              {formatCurrency(String(diskonSubtotal))}
              <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full">
                ✎
              </span>
            </button>
          </div>

          <div className="flex items-center justify-between text-sm">
            <span>PPN</span>
            <button
              onClick={() => setEditField("ppn")}
              className="flex items-center gap-2 font-medium"
            >
              {formatCurrency(String(ppn))}
              <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                ✎
              </span>
            </button>
          </div>

          <div className="pt-3 mt-3 border-t-2" />

          <div className="flex items-center justify-between text-xl">
            <span className="font-bold">TOTAL</span>
            <span className="font-bold">
              {formatCurrency(String(totalFinal))}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-[1fr_1.5fr_2fr] gap-3 mt-6">
          <Button variant="destructive" onClick={onCancel}>
            Batalkan
          </Button>
          <Button variant="secondary" onClick={onPending}>
            Pending
          </Button>
          <Button
            className="w-full font-bold"
            onClick={() =>
              navigate(
                paymentRouteMap[
                  metodePembayaran as keyof typeof paymentRouteMap
                ] || "/payment",
              )
            }
          >
            Pembayaran
          </Button>
        </div>
      </div>

      <EditModal
        isOpen={editField !== null}
        field={editField}
        currentValue={
          editField === "customer"
            ? customer || ""
            : editField === "discount"
              ? String(diskonSubtotal)
              : editField === "ppn"
                ? String(ppn)
                : ""
        }
        subtotal={editField === "ppn" ? afterDiscount : totalSubtotal}
        onClose={() => setEditField(null)}
        onSave={handleEdit}
      />
    </>
  );
}
